/**
 * ColdCallsView Component (TASK-337, was the body of DatasetsPage / TASK-288)
 *
 * The «Холодные обзвоны» tab of the Clients section: schema-driven list of
 * outbound-calling campaigns (KPI stat-row, search + status filters, list with
 * activate/pause, create dialog). Extracted as-is from DatasetsPage when the
 * campaigns moved under /clients/cold-calls (FR-001/FR-002); the inline
 * "colored dot + text" status markup was replaced by the shared <StatusDot>
 * (AC-5).
 *
 * The list is visible to operators (server-side RBAC returns own campaigns);
 * create / activate / pause / delete controls are gated behind the
 * `users.manage` permission, mirroring the backend RBAC.
 *
 * TASK-338: per-row delete with a confirm dialog (record count warning).
 *
 * KAN-595: the create dialog (kind, profile, outcome preview) moved to
 * components/datasets/CreateCampaignDialog (TASK-338/369 profile notes live there).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  CopyPlus,
  FileText,
  Loader2,
  Pause,
  Phone,
  Play,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusDot, type StatusDotColor } from '@/components/ui/status-dot';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
  CAMPAIGN_KIND_OPTIONS,
  campaignListSubtitle,
} from '@/lib/campaignKind';
import { CampaignKindBadge } from '@/components/datasets/CampaignKindBadge';
import { CreateCampaignDialog } from '@/components/datasets/CreateCampaignDialog';
import { OutreachWindowBar } from '@/components/campaigns/OutreachWindowBar';
import { datasetsApi } from '@/services/api/datasets';
import { apiClient } from '@/services/api/client';
import { useOperatorLabels } from '@/hooks/useOperatorLabels';
import { NEXT_BATCH_LABEL, useNextBatch } from '@/hooks/useNextBatch';
import { PAGE_SIZE_OPTIONS, paginateLocally, usePageSize } from '@/hooks/usePageSize';
import { PaginationBar } from '@/components/common/PaginationBar';
import type { CampaignKind, Dataset, DatasetStatus } from '@/types/dataset';

const STATUS_LABELS: Record<DatasetStatus, string> = {
  draft: 'Черновик',
  active: 'Активна',
  paused: 'Пауза',
  completed: 'Завершена',
};

// Status rendered as a colored dot + text (shared StatusDot), not a Badge.
const STATUS_COLORS: Record<DatasetStatus, StatusDotColor> = {
  draft: 'gray',
  active: 'green',
  paused: 'yellow',
  completed: 'blue',
};

interface ColdCallsViewProps {
  /** Tabs bar of the Clients section container, rendered above the content. */
  tabsBar?: React.ReactNode;
}

export function ColdCallsView({ tabsBar }: ColdCallsViewProps): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // TASK-338: campaign pending deletion (drives the confirm dialog).
  const [deleteTarget, setDeleteTarget] = useState<Dataset | null>(null);

  // Client-side filters (the list endpoint signature is unchanged).
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // KAN-593: server-side kind filter ('all' sends no ?kind=).
  const [kindFilter, setKindFilter] = useState<'all' | CampaignKind>('all');
  // KAN-731: campaigns are filtered on the client, so the page is cut here too.
  const { page, setPage, pageSize, setPageSize } = usePageSize('campaigns');
  useEffect(() => {
    setPage(1);
  }, [searchValue, statusFilter, kindFilter, setPage]);

  useEffect(() => {
    document.title = 'Контакты - кампании - SMETA AI';
  }, []);

  // RBAC: only managers (users.manage) see create / activate / pause controls.
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const canManage = currentUser?.permissions?.users?.manage === true;

  const {
    data: datasetsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['datasets', kindFilter],
    queryFn: () =>
      datasetsApi.list({
        kind: kindFilter === 'all' ? undefined : kindFilter,
        page_size: PAGE_SIZE_OPTIONS[PAGE_SIZE_OPTIONS.length - 1],
      }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => datasetsApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      toast.success('Кампания запущена');
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err, 'Не удалось запустить кампанию'));
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => datasetsApi.pause(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      toast.success('Кампания на паузе');
    },
    onError: (err: unknown) => {
      toast.error(
        extractErrorMessage(err, 'Не удалось поставить кампанию на паузу')
      );
    },
  });

  // KAN-850: next batch of a list (empty copy + upload page of the new list).
  const nextBatchMutation = useNextBatch();

  // TASK-338: delete a campaign (confirmed via the dialog below).
  const deleteMutation = useMutation({
    mutationFn: (id: string) => datasetsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      setDeleteTarget(null);
      toast.success('Кампания удалена');
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err, 'Не удалось удалить кампанию'));
    },
  });

  const handleRowClick = useCallback(
    (dataset: Dataset) => {
      navigate(`/clients/cold-calls/${dataset.id}`);
    },
    [navigate]
  );

  const datasets = useMemo(() => datasetsData?.results ?? [], [datasetsData]);
  const count = datasetsData?.count ?? 0;

  // KAN-627: the owner sees campaigns of several operators; label each row.
  const ownerIds = useMemo(() => datasets.map((d) => d.owner), [datasets]);
  const { showOperator, labelFor: operatorLabel } = useOperatorLabels(ownerIds);

  // Live KPI counters derived from the list of the selected kind (the kind
  // filter is server-side), before the search and status filters.
  const activeCount = useMemo(
    () => datasets.filter((d) => d.status === 'active').length,
    [datasets]
  );
  const pausedCount = useMemo(
    () => datasets.filter((d) => d.status === 'paused').length,
    [datasets]
  );
  const draftCount = useMemo(
    () => datasets.filter((d) => d.status === 'draft').length,
    [datasets]
  );

  // Filtered rows (search by name + status select). KPI counters above are
  // bound to the list of the selected kind, not to the search/status view.
  const filteredDatasets = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    return datasets.filter((d) => {
      const matchesSearch = !term || d.name.toLowerCase().includes(term);
      const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [datasets, searchValue, statusFilter]);
  const pageDatasets = useMemo(
    () => paginateLocally(filteredDatasets, page, pageSize),
    [filteredDatasets, page, pageSize]
  );

  const formatDate = useMemo(
    () => (value: string) =>
      new Date(value).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
      }),
    []
  );

  return (
    <>
      <PageLayout
        data-testid="datasets-page"
        title="Контакты"
        subtitle={
          <span className="hidden sm:inline">
            {campaignListSubtitle(kindFilter)}
          </span>
        }
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-white" />
          </div>
        }
        titleAddon={
          <span
            data-testid="total-count"
            className="hidden sm:inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold"
          >
            {count}
          </span>
        }
        breadcrumbs={[
          { label: 'Главная', href: '/' },
          { label: 'Контакты', href: '/clients' },
          { label: 'Холодные обзвоны' },
        ]}
        actions={
          canManage ? (
            <Button
              data-testid="create-dataset-button"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsCreateOpen(true)}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline ml-2">Новая кампания</span>
            </Button>
          ) : undefined
        }
      >
        {/* Section tabs (Контакты / Холодные обзвоны) */}
        {tabsBar}

        {/* KAN-678: окно отправки Telegram-рассылки (над таблицей кампаний) */}
        {!isLoading && !isError && <OutreachWindowBar />}

        {isLoading && (
          <div
            data-testid="datasets-loading"
            className="flex items-center justify-center py-12"
          >
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        )}

        {isError && (
          <div
            data-testid="datasets-error"
            className="flex flex-col items-center justify-center py-12"
          >
            <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ошибка загрузки кампаний
            </h3>
            <p className="text-gray-500 mb-4">
              {error instanceof Error ? error.message : 'Произошла ошибка'}
            </p>
            <Button data-testid="retry-button" onClick={() => refetch()}>
              Повторить
            </Button>
          </div>
        )}

        {!isLoading && !isError && datasets.length === 0 && (
          <div
            data-testid="datasets-empty"
            className="flex flex-col items-center justify-center py-12"
          >
            <Phone className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Кампаний пока нет
            </h3>
            {canManage && (
              <p className="text-gray-500 mb-4">Создайте первую кампанию</p>
            )}
          </div>
        )}

        {!isLoading && !isError && datasets.length > 0 && (
          <>
            {/* KPI Row */}
            <div className="flex gap-2 -mx-6 px-6 py-2 bg-white border-b border-slate-100 mb-4">
              {/* Всего кампаний */}
              <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-3.5 w-3.5 text-blue-600" />
                </div>
                <div>
                  <div className="text-[17px] font-bold text-slate-900 leading-none">
                    {count}
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">
                    Всего кампаний
                  </div>
                </div>
              </div>

              {/* Активных */}
              <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <div className="w-7 h-7 rounded-md bg-green-50 flex items-center justify-center flex-shrink-0">
                  <Play className="h-3.5 w-3.5 text-green-600" />
                </div>
                <div>
                  <div className="text-[17px] font-bold text-slate-900 leading-none">
                    {activeCount}
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">
                    Активных
                  </div>
                </div>
              </div>

              {/* На паузе */}
              <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <div className="w-7 h-7 rounded-md bg-yellow-50 flex items-center justify-center flex-shrink-0">
                  <Pause className="h-3.5 w-3.5 text-yellow-600" />
                </div>
                <div>
                  <div className="text-[17px] font-bold text-slate-900 leading-none">
                    {pausedCount}
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">
                    На паузе
                  </div>
                </div>
              </div>

              {/* Черновики */}
              <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-3.5 w-3.5 text-gray-500" />
                </div>
                <div>
                  <div className="text-[17px] font-bold text-slate-900 leading-none">
                    {draftCount}
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">
                    Черновики
                  </div>
                </div>
              </div>
            </div>

            {/* Filters: search (flex-1) + status select (right) */}
            <div className="flex items-center gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  data-testid="dataset-search-input"
                  placeholder="Поиск кампаний..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="w-full pl-9"
                />
              </div>
              <Select
                value={kindFilter}
                onValueChange={(v) => setKindFilter(v as 'all' | CampaignKind)}
              >
                <SelectTrigger
                  data-testid="dataset-kind-filter"
                  className="w-[140px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  data-testid="dataset-status-filter"
                  className="w-[160px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="draft">Черновик</SelectItem>
                  <SelectItem value="active">Активна</SelectItem>
                  <SelectItem value="paused">Пауза</SelectItem>
                  <SelectItem value="completed">Завершена</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-white rounded-lg border">
              <Table aria-label="Datasets table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Название</TableHead>
                    {showOperator && <TableHead>Оператор</TableHead>}
                    <TableHead>Профиль</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Прогресс</TableHead>
                    <TableHead>Дата</TableHead>
                    <TableHead>Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageDatasets.map((dataset) => (
                    <TableRow
                      key={dataset.id}
                      data-testid={`dataset-row-${dataset.id}`}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleRowClick(dataset)}
                    >
                      <TableCell className="font-medium">
                        <span className="mr-2">{dataset.name}</span>
                        <CampaignKindBadge kind={dataset.kind} />
                      </TableCell>
                      {showOperator && (
                        <TableCell
                          className="text-sm text-gray-600"
                          data-testid={`dataset-operator-${dataset.id}`}
                        >
                          {operatorLabel(dataset.owner)}
                        </TableCell>
                      )}
                      <TableCell className="text-sm text-gray-600">
                        {dataset.profile_label || dataset.profile_codename}
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          color={STATUS_COLORS[dataset.status]}
                          label={STATUS_LABELS[dataset.status]}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-700">
                          <span data-testid={`contacted-${dataset.id}`}>
                            {dataset.contacted}
                          </span>
                          {' / '}
                          <span data-testid={`total-${dataset.id}`}>
                            {dataset.total}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-400">
                        {formatDate(dataset.created_at)}
                      </TableCell>
                      <TableCell>
                        {canManage && (
                          <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {dataset.status !== 'active' && (
                              <Button
                                data-testid={`activate-button-${dataset.id}`}
                                variant="outline"
                                size="sm"
                                disabled={activateMutation.isPending}
                                onClick={() =>
                                  activateMutation.mutate(dataset.id)
                                }
                              >
                                <Play className="h-4 w-4 sm:mr-1" />
                                <span className="hidden sm:inline">
                                  Запустить
                                </span>
                              </Button>
                            )}
                            {dataset.status === 'active' && (
                              <Button
                                data-testid={`pause-button-${dataset.id}`}
                                variant="outline"
                                size="sm"
                                disabled={pauseMutation.isPending}
                                onClick={() => pauseMutation.mutate(dataset.id)}
                              >
                                <Pause className="h-4 w-4 sm:mr-1" />
                                <span className="hidden sm:inline">Пауза</span>
                              </Button>
                            )}
                            <Button
                              data-testid={`next-batch-button-${dataset.id}`}
                              variant="outline"
                              size="sm"
                              title={NEXT_BATCH_LABEL}
                              disabled={nextBatchMutation.isPending}
                              onClick={() => nextBatchMutation.mutate(dataset.id)}
                            >
                              <CopyPlus className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">
                                {NEXT_BATCH_LABEL}
                              </span>
                            </Button>
                            <Button
                              data-testid={`delete-button-${dataset.id}`}
                              variant="outline"
                              size="sm"
                              disabled={deleteMutation.isPending}
                              onClick={() => setDeleteTarget(dataset)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500 sm:mr-1" />
                              <span className="hidden sm:inline">Удалить</span>
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredDatasets.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={showOperator ? 7 : 6}
                        className="text-center text-gray-500 py-8"
                      >
                        Ничего не найдено
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <PaginationBar
              page={page}
              pageSize={pageSize}
              count={filteredDatasets.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              className="px-0"
            />
          </>
        )}
      </PageLayout>

      <CreateCampaignDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
      />

      {/* TASK-338: confirm campaign deletion */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent data-testid="delete-dataset-dialog">
          <DialogHeader>
            <DialogTitle>Удалить кампанию?</DialogTitle>
            <DialogDescription>
              Кампания «{deleteTarget?.name}» и все её записи (
              {deleteTarget?.total ?? 0}) будут удалены. Это действие
              необратимо.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Отмена
            </Button>
            <Button
              data-testid="confirm-delete-button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id);
                }
              }}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ColdCallsView;
